/**
 * Shared output helpers for `wayai conversations <id> observability` (hub-scoped)
 * and `wayai admin observability <hub> <conv>` (platform-admin). Output shape is
 * identical so an operator switching between the two commands sees the same
 * format — only the data source (and the API path) differs.
 */

import type { ApiClient } from '../lib/api-client.js';
import { ApiError } from '../lib/api-client.js';
import type { LLMObservabilityRecord, WayAIMetadataRecord } from '@wayai/core';

interface RenderOptions {
  messageId?: string;
  jsonOutput: boolean;
}

export async function runConversationObservability(
  client: ApiClient,
  hubId: string,
  conversationId: string,
  opts: RenderOptions,
): Promise<void> {
  // The live route reads the conversation in the hub named here, and answers 404 once its
  // ConversationDO is cleaned up (24h after archival). R2 records outlive the DO, so on 404 we
  // retry through the archive route (CH+HubDO auth) before declaring the data missing.
  if (opts.messageId) {
    const result = await fallbackOn404(
      () => client.getMessageObservability(hubId, conversationId, opts.messageId!),
      () => client.getArchivedMessageObservability(hubId, conversationId, opts.messageId!),
    );
    renderRecord(result.data, opts.jsonOutput);
    return;
  }

  const result = await fallbackOn404(
    () => client.listConversationObservability(hubId, conversationId),
    () => client.listArchivedConversationObservability(hubId, conversationId),
  );
  renderList({
    hubId: result.data.hub_id,
    conversationId: result.data.conversation_id,
    entries: result.data.entries,
    truncated: result.data.truncated,
    jsonOutput: opts.jsonOutput,
  });
}

async function fallbackOn404<T>(primary: () => Promise<T>, fallback: () => Promise<T>): Promise<T> {
  try {
    return await primary();
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return await fallback();
    }
    throw err;
  }
}

export async function runDebugObservability(
  client: ApiClient,
  hubId: string,
  conversationId: string,
  opts: RenderOptions,
): Promise<void> {
  const result = await client.debugReadObservability(hubId, conversationId, {
    messageId: opts.messageId,
  });

  if (result.data.kind === 'single') {
    renderRecord(result.data.record, opts.jsonOutput);
    return;
  }

  if (opts.messageId) {
    // Defensive — backend returns 404 in this case, but guard against contract drift.
    throw new ApiError('GET', '/observability', 404, 'Observability record missing');
  }

  renderList({
    hubId: result.data.hub_id,
    conversationId: result.data.conversation_id,
    entries: result.data.entries,
    truncated: result.data.truncated,
    jsonOutput: opts.jsonOutput,
  });
}

function renderRecord(data: LLMObservabilityRecord, jsonOutput: boolean): void {
  if (jsonOutput) {
    console.log(JSON.stringify(data, null, 2));
    return;
  }

  // Default mode prints a compact summary so the terminal isn't flooded with
  // multi-KB system prompts. --json gets the full record.
  const obs = data.observability;
  const meta = data.metadata;

  const provider = obs['gen_ai.system'] ?? '-';
  const requestModel = obs['gen_ai.request.model'] ?? '-';
  const responseModel = obs['gen_ai.response.model'];
  const inputTokens = obs['gen_ai.usage.input_tokens'] ?? 0;
  const outputTokens = obs['gen_ai.usage.output_tokens'] ?? 0;
  const cacheRead = obs['gen_ai.usage.cache_read_input_tokens'];
  const cacheWrite = obs['gen_ai.usage.cache_creation_input_tokens'];

  console.log(`message_id:    ${meta.message_id ?? '-'}`);
  console.log(`timestamp:     ${meta.timestamp ?? '-'}`);
  console.log(`agent:         ${meta.agent_id ?? '-'}${meta.agent_role ? ` (${meta.agent_role})` : ''}`);
  console.log(`provider:      ${provider}`);
  console.log(`model:         ${requestModel}${responseModel && responseModel !== requestModel ? ` (responded as ${responseModel})` : ''}`);
  console.log(`tokens:        in=${inputTokens} out=${outputTokens}${cacheRead != null ? ` cache_read=${cacheRead}` : ''}${cacheWrite != null ? ` cache_write=${cacheWrite}` : ''}`);
  console.log(`latency_ms:    ${meta.latency_ms ?? '-'}`);
  if (meta.tool_iterations != null) console.log(`tool_iters:    ${meta.tool_iterations}`);
  if (meta.tool_calls_total != null) console.log(`tool_calls:    ${meta.tool_calls_total}`);
  if (meta.system_reinvoke) console.log('reinvoke:      yes');
  if (meta.error) console.log(`error:         ${meta.error}`);

  console.log('');
  console.log('Pass --json for full prompt/completion/tool-call payloads.');
}

interface RenderListArgs {
  hubId: string;
  conversationId: string;
  entries: WayAIMetadataRecord[];
  truncated: boolean;
  jsonOutput: boolean;
}

function renderList(args: RenderListArgs): void {
  if (args.jsonOutput) {
    console.log(JSON.stringify({
      hub_id: args.hubId,
      conversation_id: args.conversationId,
      entries: args.entries,
      truncated: args.truncated,
    }, null, 2));
    return;
  }

  console.log(`hub_id:         ${args.hubId}`);
  console.log(`conversation_id:${args.conversationId}`);
  console.log(`messages:       ${args.entries.length}${args.truncated ? ' (truncated)' : ''}`);

  if (args.entries.length === 0) {
    console.log('');
    console.log('(no observability records — only assistant LLM turns produce them)');
    return;
  }

  console.log('');
  console.log('timestamp                | message_id                            | agent_role        | latency_ms | tool_calls | error');
  console.log('-------------------------|---------------------------------------|-------------------|------------|------------|------');
  for (const e of args.entries) {
    const ts = (e.timestamp ?? '-').padEnd(24);
    const mid = (e.message_id ?? '-').padEnd(36);
    const role = (e.agent_role ?? '-').slice(0, 17).padEnd(17);
    const latency = String(e.latency_ms ?? '-').padStart(10);
    const toolCalls = String(e.tool_calls_total ?? '-').padStart(10);
    const err = e.error ? 'yes' : '';
    console.log(`${ts} | ${mid} | ${role} | ${latency} | ${toolCalls} | ${err}`);
  }
  console.log('');
  console.log('Drill in:  --message-id <id>  (for full prompt/completion/tool-calls; add --json for raw)');
}
